import { useContext, useEffect, useState } from 'react';
import {
    Navigate,
    Route,
    BrowserRouter as Router,
    Routes,
    useLocation,
    useNavigate,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAccount, WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@rainbow-me/rainbowkit/styles.css';
import './App.css';
import {
    ConnectWalletContext,
    CPGAddress,
    TokenContext,
    WalletAddressContext,
} from './constant/constant';
import AdminDashboard from './pages/adminPanel/admin-dashboard/AdminDashboard';
import AdminContractAnalytics from './pages/adminPanel/AdminContractAnalytics';
import AdminDispute from './pages/adminPanel/admin-dispute/AdminDispute';
import AdminDisputeDetailPage from './pages/adminPanel/admin-dispute/AdminDisputeDetailPage';
import Home from './pages/dashboard/Home';
import Dispute from './pages/dispute/Dispute';
import ReportProblem from './pages/dispute/ReportProblem';
import { DOCS_PATH, JOBS_PORTAL_PATH } from './config/paths';
import DocsLayout from './pages/docs/DocsLayout';
import DocsPage from './pages/docs/DocsPage';
import DocsIndexRedirect from './pages/docs/DocsIndexRedirect';
import CompanyJob from './pages/companyJob/CompanyJob';
import GetPaid from './pages/getPaid/GetPaid';
import Header from './pages/header/Header';
import JobsPortalHeader from './pages/header/JobsPortalHeader';
import JobsPortalNavbar from './pages/header/JobsPortalNavbar';
import Navbar from './pages/header/Navbar';
import Login from './pages/login/Login';
import NotFound from './pages/notFound/NotFound';
import OnGoing from './pages/onGoing/OnGoing';
import Pay from './pages/pay/Pay';
import AccountRegistration from './pages/registration/AccountRegistration';
import { config } from './utils/wagmiConfig';
import { initialTokenList } from './constant/constant';
import 'react-loading-skeleton/dist/skeleton.css';
import 'react-tooltip/dist/react-tooltip.css';
import { readContractData } from './utils/contractInstance';
import { CPGABI } from './ABI/ABI';
import {
    isWalletCpgAdmin,
    resolveCpgAdminAddresses,
} from './utils/cpgDiamondReader';
import { JobsPortalRoleProvider } from './context/JobsPortalRoleContext';
import { NotificationProvider } from './context/NotificationContext';
import { ThemeProvider } from './context/ThemeContext';
import DocumentTitle from './components/DocumentTitle';
import PostConnectNetworkSetup from './components/connectWallet/PostConnectNetworkSetup';
import { ThemedRainbowKitProvider } from './providers/ThemedRainbowKitProvider';

const CompanyJobToJobsPortalRedirect = () => {
    const { search } = useLocation();
    return <Navigate to={{ pathname: JOBS_PORTAL_PATH, search }} replace />;
};

const PrivateRoute = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const userData = localStorage.getItem('userData');
        const isLoginRoute = /^\/login\/?$/.test(location.pathname);

        if (!userData) {
            localStorage.setItem('redirectPath', location.pathname);
            navigate('/login');
        } else if (userData && isLoginRoute) {
            const redirectPath = localStorage.getItem('redirectPath') || '/';
            localStorage.removeItem('redirectPath');
            navigate(redirectPath);
        }
    }, [location.pathname, navigate]);

    return children;
};

const Layout = ({ children }) => {
    const { isAdmin, setIsAdmin, setAdminWalletAddress, adminWalletAddress } =
        useContext(WalletAddressContext);
    const [isInitialized, setIsInitialized] = useState(false);
    const { address: walletAddress } = useAccount();
    const loc = useLocation();

    const userRoutes = [
        '/',
        '/dashboard',
        '/pay',
        '/get-paid',
        '/company-job',
        '/dispute',
        '/on-going',
        '/dispute/report-problem',
        JOBS_PORTAL_PATH,
        DOCS_PATH,
    ];
    const adminRoutes = [
        '/',
        '/dashboard',
        '/admin-dashboard',
        '/admin-contract-analytics',
        '/admin-dispute',
        '/pay',
        '/get-paid',
        JOBS_PORTAL_PATH,
        '/dispute',
        '/on-going',
        '/dispute/report-problem',
        DOCS_PATH,
    ];

    const loadCpgAdminAddresses = async () => {
        if (!CPGAddress) {
            setAdminWalletAddress('');
            setIsAdmin(false);
            return;
        }
        try {
            const admins = await resolveCpgAdminAddresses(
                readContractData,
                CPGAddress,
                CPGABI,
            );
            setAdminWalletAddress(admins.primary || '');
            setIsAdmin(isWalletCpgAdmin(walletAddress, admins));
        } catch (error) {
            console.log(error.message);
            setAdminWalletAddress('');
            setIsAdmin(false);
        }
    };

    useEffect(() => {
        loadCpgAdminAddresses();
    }, [walletAddress]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setIsInitialized(true);
    }, [isAdmin, loc.pathname]);

    if (!isInitialized) return null;

    const allowedRoutes = isAdmin ? adminRoutes : userRoutes;

    const isDocsRoute =
        loc.pathname === DOCS_PATH || loc.pathname.startsWith(`${DOCS_PATH}/`);

    const isAdminDisputeDetailRoute =
        /^\/admin-dispute\/[^/]+$/.test(loc.pathname);

    // Check if current route is valid
    const isValidRoute =
        isDocsRoute ||
        (isAdmin && isAdminDisputeDetailRoute) ||
        allowedRoutes.some((route) =>
            new RegExp(`^${route}(/)?$`).test(loc.pathname),
        );

    // LOGIC: Hide navigation if route is invalid OR if we are on the Root/Landing page
    const isRootPath = loc.pathname === '/';
    const hideNav =
        !isValidRoute || isRootPath || loc.pathname === '/login' || isDocsRoute;

    if (!isValidRoute && loc.pathname !== '/login') {
        return <NotFound />;
    }

    const onJobsPortal = loc.pathname === JOBS_PORTAL_PATH;

    return (
        <>
            {!hideNav && (onJobsPortal ? <JobsPortalNavbar /> : <Navbar />)}
            {!hideNav && (onJobsPortal ? <JobsPortalHeader /> : <Header />)}
            <div
                className={
                    !hideNav
                        ? 'lg:ml-[265px] lg:max-w-full'
                        : isDocsRoute
                          ? 'w-full max-w-none'
                          : ''
                }
            >
                <div className={isDocsRoute ? 'w-full' : 'lg:mx-auto'}>{children}</div>
            </div>
        </>
    );
};

function App() {
    const [connected, setConnected] = useState(false);
    const [walletAddress, setWalletAddress] = useState('');
    const [adminWalletAddress, setAdminWalletAddress] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [matchedNetId, setMatchedNetId] = useState(true);
    const [tokenList, setTokenList] = useState(initialTokenList);
    const queryClient = new QueryClient();

    return (
        <WagmiProvider config={config}>
            <QueryClientProvider client={queryClient}>
                <ThemeProvider>
                    <ThemedRainbowKitProvider>
                    <PostConnectNetworkSetup />
                    <ConnectWalletContext.Provider
                        value={{
                            connected,
                            setConnected,
                            matchedNetId,
                            setMatchedNetId,
                        }}
                    >
                        <WalletAddressContext.Provider
                            value={{
                                walletAddress,
                                setWalletAddress,
                                adminWalletAddress,
                                setAdminWalletAddress,
                                isAdmin,
                                setIsAdmin,
                            }}
                        >
                            <TokenContext.Provider
                                value={{ tokenList, setTokenList }}
                            >
                                <Router>
                                    <DocumentTitle />
                                    <NotificationProvider>
                                    <JobsPortalRoleProvider>
                                    <Layout>
                                        <Routes>
                                            {/* <Route exact path='/' element={<LandingPage />} /> */}
                                            <Route
                                                exact
                                                path="/dashboard"
                                                element={
                                                    <PrivateRoute>
                                                        <Home />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                exact
                                                path="/pay"
                                                element={
                                                    <PrivateRoute>
                                                        <Pay />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                exact
                                                path="/get-paid"
                                                element={
                                                    <PrivateRoute>
                                                        <GetPaid />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                exact
                                                path="/company-job"
                                                element={
                                                    <PrivateRoute>
                                                        <CompanyJobToJobsPortalRedirect />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                exact
                                                path={JOBS_PORTAL_PATH}
                                                element={
                                                    <PrivateRoute>
                                                        <CompanyJob />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route path={DOCS_PATH} element={<DocsLayout />}>
                                                <Route index element={<DocsIndexRedirect />} />
                                                <Route path=":slug" element={<DocsPage />} />
                                            </Route>
                                            <Route
                                                exact
                                                path="/"
                                                element={<Login />}
                                            />
                                            <Route
                                                exact
                                                path="/dispute"
                                                element={
                                                    <PrivateRoute>
                                                        <Dispute />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                exact
                                                path="/on-going"
                                                element={
                                                    <PrivateRoute>
                                                        <OnGoing />
                                                    </PrivateRoute>
                                                }
                                            />

                                            <Route
                                                exact
                                                path="/admin-dashboard"
                                                element={
                                                    <PrivateRoute>
                                                        <AdminDashboard />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                exact
                                                path="/admin-contract-analytics"
                                                element={
                                                    <PrivateRoute>
                                                        <AdminContractAnalytics />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                exact
                                                path="/admin-dispute"
                                                element={
                                                    <PrivateRoute>
                                                        <AdminDispute />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                path="/admin-dispute/:disputeId"
                                                element={
                                                    <PrivateRoute>
                                                        <AdminDisputeDetailPage />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                exact
                                                path="/create-account"
                                                element={
                                                    <PrivateRoute>
                                                        <AccountRegistration />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                exact
                                                path="/dispute/report-problem"
                                                element={
                                                    <PrivateRoute>
                                                        <ReportProblem />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                path="*"
                                                element={<NotFound />}
                                            />
                                        </Routes>
                                        <ToastContainer
                                            theme="dark"
                                            position="bottom-right"
                                        />
                                    </Layout>
                                    </JobsPortalRoleProvider>
                                    </NotificationProvider>
                                </Router>
                            </TokenContext.Provider>
                        </WalletAddressContext.Provider>
                    </ConnectWalletContext.Provider>
                    </ThemedRainbowKitProvider>
                </ThemeProvider>
            </QueryClientProvider>
        </WagmiProvider>
    );
}

export default App;
